import { watch } from 'vue'
import platform from 'platform'
import {
	createAnalytics,
	MeasurementSystem,
	MedicationName,
	eventInt,
	eventTimestamp,
	type Storage,
} from '@simple-life-medical/med-analytics-ts-web'

import { ENV_DEV_OR_STAGING, VUE_APP_SEMVERSION } from '@/helpers/environment'
import { toE164 } from '@/helpers/phone'
import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { asMedicationKind, type MedicationKindEnum } from '@/domain/medication'
import { withDeadline } from '@/helpers/deadline'

// Re-export schema enums and number-field converters that call-sites need for typed
// payloads, so no module other than this one imports the analytics lib directly
// (docs/med-analytics-ts.md §8.4).  Since v0.2.x the lib requires numeric event/context
// fields to be branded: eventInt() for integers (truncates toward zero + safe-integer
// check) and eventDecimal() for decimals (validates a decimal string).  v0.3.0 added a
// third brand, eventTimestamp(), for schema `timestamp` fields: it takes a Date and emits
// epoch milliseconds, so a raw number, eventInt() or seconds value is no longer assignable
// to those fields (structurally prevents the seconds-vs-ms class of bug).
export {
	Gender,
	OnboardingScreenType,
	OnboardingProductType,
	MeasurementSystem,
	// DEV-35019 / схема 0.3.16: ASYNC | SYNC на OnboardingFunnelCompletedDone —
	// одобрит ли клиницист рецепт заочно или после телефонного визита.
	PrescriptionType,
	eventInt,
	eventDecimal,
	eventTimestamp,
} from '@simple-life-medical/med-analytics-ts-web'

// DEV-35411: the schema types `phone` fields as integer — E.164 digits without the '+'
// (1XXXXXXXXXX for US). Implausible input → undefined, so the field is omitted.
export function phoneEventInt(raw: unknown) {
	const e164 = toE164(raw)
	return e164 ? eventInt(Number(e164.slice(1))) : undefined
}

type AnalyticsInstance = ReturnType<typeof createAnalytics>

const apiKey: string = import.meta.env.VITE_MED_ANALYTICS_KEY ?? ''
const apiUrl: string = import.meta.env.VITE_MED_ANALYTICS_URL ?? ''

const isPreviewMode = (() => {
	if (!ENV_DEV_OR_STAGING) return false
	try {
		return new URLSearchParams(window.location.search).has('is_ob_preview')
	} catch {
		return false
	}
})()

const localStorageAdapter: Storage = {
	get(key) {
		try {
			return localStorage.getItem(key)
		} catch {
			return null
		}
	},
	set(key, value) {
		try {
			localStorage.setItem(key, value)
		} catch {
			/* ignore quota / disabled storage */
		}
	},
	remove(key) {
		try {
			localStorage.removeItem(key)
		} catch {
			/* ignore */
		}
	},
}

// No-op singleton matching the real `Analytics` shape — used when env vars
// missing or in preview-mode.  Keeps call-sites safe without runtime checks.
const noopAnalytics: AnalyticsInstance = {
	setContext: () => {},
	track: () => {},
	flush: () => Promise.resolve(),
}

// --- Dev/staging console logger (analogue of Jet's #logger) ----------------
// Wraps track()/setContext() to mirror calls to DevTools console with grouping
// and a per-category color, so devs see *what they sent* without decoding
// protobuf bodies in the Network panel.  Stripped at build time in prod (the
// wrap is gated on ENV_DEV_OR_STAGING).
const LOGGER_COLORS: Record<'default' | 'ux' | 'onboarding' | 'paywall', string> = {
	default: '#7c3aed', // purple — SessionStart / generic
	ux: '#0ea5e9', // blue — ScreenView / ButtonClick / Block* / Overlay*
	onboarding: '#10b981', // green — Onboarding* + Utm + CookieConsent + UserRegistration
	paywall: '#f59e0b', // amber — Paywall* / Checkout* / Payment*
}

const UX_EVENTS = new Set(['ScreenView', 'ButtonClick', 'BlockView', 'BlockClick', 'OverlayView', 'OverlayClose'])

function pickColor(eventName: string): string {
	if (eventName.includes('Paywall') || eventName.includes('Checkout') || eventName.includes('Payment')) {
		return LOGGER_COLORS.paywall
	}
	if (
		eventName.startsWith('Onboarding') ||
		eventName === 'UtmParamsParsingDone' ||
		eventName === 'CookieConsentDone' ||
		eventName === 'UserRegistrationData'
	) {
		return LOGGER_COLORS.onboarding
	}
	if (UX_EVENTS.has(eventName)) return LOGGER_COLORS.ux
	return LOGGER_COLORS.default
}

function logTrack(eventName: string, payload: unknown, header: unknown): void {
	const color = pickColor(eventName)
	/* eslint-disable no-console */
	console.groupCollapsed(
		`%cmed-analytics [debug]: %c${eventName}`,
		'font-weight: 400; color: #888',
		`color: ${color}; font-weight: 600`,
	)
	console.log('payload:', payload)
	if (header !== undefined) console.log('header:', header)
	console.groupEnd()
	/* eslint-enable no-console */
}

function logSetContext(partial: unknown): void {
	/* eslint-disable no-console */
	console.groupCollapsed(
		`%cmed-analytics [debug]: %csetContext`,
		'font-weight: 400; color: #888',
		`color: ${LOGGER_COLORS.default}; font-style: italic`,
	)
	console.log(partial)
	console.groupEnd()
	/* eslint-enable no-console */
}

/**
 * Mutates `instance` to log every track()/setContext() to the DevTools console.
 *
 * The methods are intentionally type-erased to `(...args) => void` so we can wrap
 * them without `as` casts.  Runtime contract is preserved — origTrack/origSetContext
 * are bound to the original instance, so they still receive their original args.
 */
type ErasedFn = (...args: unknown[]) => void

function attachDebugLogger(instance: AnalyticsInstance): void {
	const origTrack: ErasedFn = instance.track.bind(instance)
	const origSetContext: ErasedFn = instance.setContext.bind(instance)

	const wrappedTrack: ErasedFn = (...args) => {
		logTrack(String(args[0]), args[1], args[2])
		origTrack(...args)
	}
	const wrappedSetContext: ErasedFn = (...args) => {
		logSetContext(args[0])
		origSetContext(...args)
	}

	Object.assign(instance, { track: wrappedTrack, setContext: wrappedSetContext })
}

function createInstance(): AnalyticsInstance {
	if (isPreviewMode || !apiKey || !apiUrl) return noopAnalytics

	const instance = createAnalytics({
		apiKey,
		apiUrl,
		storage: localStorageAdapter,
	})

	if (ENV_DEV_OR_STAGING) attachDebugLogger(instance)

	instance.setContext({
		application: {
			app_id: 'WebFunnel',
			app_platform: 'Web',
			app_version: VUE_APP_SEMVERSION,
			app_language: window.language,
		},
		browser: {
			browser_name: platform.name,
			browser_version: platform.version,
		},
		os: {
			os_name: platform.os?.family,
			os_version: platform.os?.version,
		},
	})

	// v0.2.x dropped the explicit `SessionStart` event from the web schema.  The SDK's
	// SessionManager assigns session_id/seq_num to every event and the backend derives
	// sessions from those, so no bootstrap event is needed (and emitting one now throws
	// `encoders['SessionStart'] is not a function` at runtime).
	return instance
}

const analytics = createInstance()

/**
 * Best-effort delivery before the page is torn down (deeplink into the
 * app, OneLink → store). The SDK persists batches to localStorage only inside flush(), and a
 * terminal screen has no next visit to replay them, so the leaving code path must try to
 * deliver while the page is still alive — but bounded: with the telemetry host down the
 * transport retries 5×10 s + 5×60 s and an unbounded await would trap the user on the exit.
 * flush() never rejects (the transport swallows), so the race only ever resolves.
 */
export function flushBounded(timeoutMs = 3000): Promise<void> {
	return withDeadline(analytics.flush(), timeoutMs, undefined)
}

export default analytics

// --- Context watchers --------------------------------------------------------
// Subscribe to Pinia state changes that map onto schema `Context*` fields, and
// keep med-analytics context in sync.  Helpers stay private to this module so
// no other code needs to know about med-analytics context shape.
//
// Call `initContextWatchers()` from main.ts AFTER `app.use(pinia)` — `useStore`
// requires an active Pinia instance and the watch() needs a running effect scope.

let lastSentMeasurement: MeasurementSystem | null = null
function syncMeasurement(unit: string | null | undefined): void {
	if (!unit) return
	const ms = unit === 'ft' || unit === 'lbs' ? MeasurementSystem.IMPERIAL : MeasurementSystem.METRIC
	if (ms === lastSentMeasurement) return
	analytics.setContext({ user: { user_measurement_system: ms } })
	lastSentMeasurement = ms
}

// `onboarding.onboarding_funnel_id` (added to the schema in v0.2.9) identifies which funnel
// the session belongs to — e.g. 'ob-kova-glp-1-app'.  The id is the location store's resolved
// appAreaConfig.funnel.id, which both entry paths populate via storeInitialPath(): a landing
// visit (App.vue created) and a CRM-mailing redirect into the paywall (Redirect.vue, /redirect).
// setContext deep-merges, so setting just this field preserves the seeded onboarding_session_id
// / onboarding_start_ts.
let lastSentFunnelId: string | null = null
function syncFunnelId(id: string | null | undefined): void {
	if (!id || id === lastSentFunnelId) return
	analytics.setContext({ onboarding: { onboarding_funnel_id: id } })
	lastSentFunnelId = id
}

export function initContextWatchers(): void {
	const mainStore = useMainStore()
	const locationStore = useLocationStore()

	// Seed the onboarding context once, at the earliest point Pinia is active, so both
	// fields ride on every onboarding event (id = per-session uuid, start_ts = app boot).
	analytics.setContext({
		onboarding: {
			onboarding_session_id: mainStore.sessionId,
			onboarding_start_ts: eventTimestamp(new Date()),
		},
	})

	// getHeightUnitSystem is a Pinia getter that resolves user override (survey value)
	// over geo default ('ft' for US, 'cm' otherwise).  `immediate: true` covers the
	// initial geo-default fire; subsequent reactivity covers user-override changes.
	watch(() => mainStore.getHeightUnitSystem, syncMeasurement, { immediate: true })

	// onboarding_funnel_id rides every onboarding event.  appAreaConfig is resolved into the
	// store by storeInitialPath(), which runs after this from whichever entry path the user
	// took (landing → App.vue created, or CRM /redirect → Redirect.vue), so a watcher — not a
	// one-shot read here — is what captures the real funnel id once either path lands it.
	// Returning users with a persisted store get it synchronously on the immediate fire.
	watch(() => locationStore.appAreaConfig?.funnel?.id, syncFunnelId, { immediate: true })
}

// --- Medication context (DEV-33795) ------------------------------------------
// `user.user_medication` is a User context property (schema enum MedicationName) stamped
// when a paywall opens, so OnboardingPaywallShow and later paywall events carry the
// medication.  Unlike syncMeasurement this isn't a Pinia watcher — PaywallView calls it
// imperatively from trackAnalyticsShowScreenEvents() with the paywall store's `medication`
// (set from the instance config: 'semaglutide' | 'tirzepatide').
const MEDICATION_BY_CHOICE: Record<MedicationKindEnum, MedicationName> = {
	semaglutide: MedicationName.SEMAGLUTIDE,
	tirzepatide: MedicationName.TIRZEPATIDE,
}

// Schema `medication` fields (OnboardingMedicationChoiceDone) take the MedicationName enum.
// Exported as a converter so call-sites keep passing the funnel's kind string and this module
// stays the only one importing the lib.
export function toMedicationName(choice: string | null | undefined): MedicationName | undefined {
	const kind = asMedicationKind(choice)
	return kind ? MEDICATION_BY_CHOICE[kind] : undefined
}

let lastSentMedication: MedicationName | null = null
export function syncMedication(choice: string | null | undefined): void {
	const kind = asMedicationKind(choice)
	if (!kind) return
	const medication = MEDICATION_BY_CHOICE[kind]
	if (medication === lastSentMedication) return
	analytics.setContext({ user: { user_medication: medication } })
	lastSentMedication = medication
}
